import { STAFF_ROLES } from './constants.js';
/**
 * Tenant-staff capabilities — the RBAC matrix from docs/06-tenant-admin.md.
 * Routes ask for a capability, never for a role, so the matrix stays the single
 * place where "who may do what" is decided.
 *
 * Shared between the API (which enforces it) and the tenant console (which used to keep a
 * second copy to decide what to render, and silently drifted from this one — see DECISIONS
 * D77). The console no longer holds an opinion: it renders from capabilities the server
 * already resolved and sent back.
 */
export const CAPABILITIES = [
    'dashboard:view',
    'bookings:view',
    'catalog:manage',
    'trips:operate',
    'disputes:manage',
    'refunds:issue',
    // Confirming that cash is in the till is the ONLY thing that turns a cash
    // claim into a paid seat, so it is its own capability rather than folded into
    // bookings:view. An OPERATOR at a counter is exactly who does it — and exactly
    // who must not also be able to issue refunds.
    'cash:review',
    'branding:manage',
    'settings:manage',
    'staff:manage',
    'billing:view',
];
/**
 * The four roles every tenant starts with. Once a tenant can define its own roles
 * (`TenantRole`), these are seed data and a reset target — "what OWNER means" is no longer
 * compiled in, it is what the row for OWNER currently holds, defaulted from this list when
 * the row is created and restored from it on "Reset to default".
 */
export const BUILT_IN_ROLE_CAPABILITIES = {
    OWNER: [...CAPABILITIES],
    MANAGER: [
        'dashboard:view',
        'bookings:view',
        'catalog:manage',
        'trips:operate',
        'disputes:manage',
        'refunds:issue',
        'cash:review',
        'branding:manage',
        'settings:manage',
    ],
    OPERATOR: ['dashboard:view', 'bookings:view', 'trips:operate', 'cash:review'],
    SUPPORT: ['dashboard:view', 'bookings:view', 'disputes:manage'],
};
export const BUILT_IN_ROLE_DESCRIPTIONS = {
    OWNER: 'Everything, including staff and billing',
    MANAGER: 'Operations, network, branding and refunds',
    OPERATOR: 'Departures and check-in',
    SUPPORT: 'Bookings and passenger requests',
};
export const BUILT_IN_ROLE_NAMES = {
    OWNER: 'Owner',
    MANAGER: 'Manager',
    OPERATOR: 'Operator',
    SUPPORT: 'Support',
};
/**
 * One row per built-in, carrying everything a `TenantRole` needs at creation time or at
 * reset time. The single place this data is written in TypeScript — the migration that
 * backfills existing tenants duplicates it in SQL only because a migration file cannot
 * import this module.
 */
export const BUILT_IN_ROLE_DEFAULTS = STAFF_ROLES.map((key) => ({
    key,
    name: BUILT_IN_ROLE_NAMES[key],
    description: BUILT_IN_ROLE_DESCRIPTIONS[key],
    capabilities: BUILT_IN_ROLE_CAPABILITIES[key],
}));
/** Whether a built-in role, by its compiled matrix, grants a capability. */
export function can(role, capability) {
    const allowed = BUILT_IN_ROLE_CAPABILITIES[role];
    return allowed ? allowed.includes(capability) : false;
}
/** The full capability list a built-in role grants by its compiled matrix. */
export function capabilitiesForBuiltInRole(role) {
    return [...(BUILT_IN_ROLE_CAPABILITIES[role] ?? [])];
}
//# sourceMappingURL=rbac.js.map